import styled from "styled-components";
import { formatDate } from "@shared/utils/date";
import Col from "antd/es/col";
import Row from "antd/es/row";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { PrecisionType, TradeExtraInfoActivity } from "@shared/types";
import { useEffect } from "react";
import useMeasure from "utils/hooks/useMeasure";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex.tsx";
import { hexToRGB } from "@shared/utils/misc.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import PointAndLine from "features/trades/TradeDrawer/Timeline/PointAndLine.tsx";

interface ActivityProps {
  activity: TradeExtraInfoActivity;
  priceCoin?: string;
  color: string;
  textColor: string;
  pricePrecision?: number;
  setItemHeight?: (height: number) => void;
}

const Activity = ({
  activity,
  color,
  textColor,
  priceCoin,
  pricePrecision,
  setItemHeight,
}: ActivityProps) => {
  const [measureRef, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    if (height) {
      setItemHeight?.(height);
    }
  }, [height]);

  return (
    <Flex>
      <Flex style={{ paddingTop: 20 }}>
        <ActivityTag $color={color} $textColor={textColor}>
          {activity.description}
        </ActivityTag>
        <HorizontalLine />
      </Flex>
      <VerticalLine>
        <PointAndLine color={color} />
        <ActivityCard ref={measureRef}>
          <TimeRow>{formatDate(activity.relevant_time, true, true)}</TimeRow>
          <Row style={{ justifyContent: "space-between" }}>
            <Col span={12}>{activity.status}</Col>
            <Col
              span={12}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {activity.execution_price ? (
                <TextWithPrecisionAndIcon
                  text={activity.execution_price}
                  precisionType={PrecisionType.PRICE}
                  precisionCoin={priceCoin}
                  precision={pricePrecision}
                  iconType={ICON_TYPES.COIN}
                  coinOrExchangeName={priceCoin}
                  justify="flex-end"
                />
              ) : (
                <Text>-</Text>
              )}
            </Col>
          </Row>
        </ActivityCard>
      </VerticalLine>
    </Flex>
  );
};

export default Activity;

const VerticalLine = styled.div`
  width: 100%;
  border-left: ${(p) => p.theme.borders.medium};
  display: flex;
  align-items: center;
  padding-top: 20px;
`;
const ActivityCard = styled.div`
  height: fit-content;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px 20px;
  position: relative;
  border-radius: 12px;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.textColor};
  overflow: hidden;
  ${(p) =>
    !p.theme.isDarkMode &&
    `
  box-shadow: ${p.theme.shadows.card};
  `}
`;
const TimeRow = styled.div`
  width: 100%;
  border-bottom: 0.2px solid ${(p) => p.theme.colors.lightestBorderGrey};
  text-align: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const HorizontalLine = styled.div`
  width: 35px;
  border-top: ${(p) => p.theme.borders.medium};
  @media (max-width: ${MOBILE_SEARCH}) {
    width: 20px;
  }
`;
const ActivityTag = styled(Flex)<{ $color: string; $textColor: string }>`
  width: 100px;
  min-height: 42px;
  justify-content: center;
  border-radius: 8px;
  color: ${(p) => p.$textColor};
  background: ${(p) => hexToRGB(p.$color, 0.1)};
  padding: 8px;
  text-align: center;

  @media (max-width: ${MOBILE_SEARCH}) {
    width: 55px;
  }
`;
